import type { OchalModuleManifest } from '@ochal/module-sdk/contracts';
import { satisfiesVersion } from './semver';

export interface ResolutionIssue { moduleId: string; reason: string }
export interface ResolutionResult { order: readonly OchalModuleManifest[]; issues: readonly ResolutionIssue[] }
export function resolveDependencies(manifests: readonly OchalModuleManifest[]): ResolutionResult {
  const byId = new Map(manifests.map((manifest) => [manifest.id, manifest]));
  const issues: ResolutionIssue[] = [];
  const invalid = new Set<string>();
  for (const manifest of manifests) for (const dependency of manifest.dependencies ?? []) {
    const target = byId.get(dependency.id);
    if (!target && !dependency.optional) { issues.push({ moduleId: manifest.id, reason: `Missing dependency ${dependency.id}` }); invalid.add(manifest.id); }
    else if (target && !satisfiesVersion(target.version, dependency.version) && !dependency.optional) { issues.push({ moduleId: manifest.id, reason: `Incompatible dependency ${dependency.id}@${target.version}; expected ${dependency.version}` }); invalid.add(manifest.id); }
  }
  const order: OchalModuleManifest[] = [];
  const visiting = new Set<string>();
  const visited = new Set<string>();
  function visit(manifest: OchalModuleManifest, path: readonly string[]): void {
    if (invalid.has(manifest.id) || visited.has(manifest.id)) return;
    if (visiting.has(manifest.id)) {
      const cycle = [...path, manifest.id];
      for (const id of cycle) invalid.add(id);
      issues.push({ moduleId: manifest.id, reason: `Dependency cycle: ${cycle.join(' -> ')}` });
      return;
    }
    visiting.add(manifest.id);
    for (const dependency of manifest.dependencies ?? []) { const target = byId.get(dependency.id); if (target) visit(target, [...path, manifest.id]); }
    visiting.delete(manifest.id); visited.add(manifest.id);
    if (!invalid.has(manifest.id)) order.push(manifest);
  }
  for (const manifest of manifests) visit(manifest, []);
  return { order: order.filter((manifest) => !invalid.has(manifest.id)), issues };
}
